import React, { useEffect, useState } from 'react';

import Button, { ButtonGroup } from '@atlaskit/button';
import PremiumIcon from '@atlaskit/icon/glyph/premium';

import { StorageOfUniqueServices } from '@qality/common-service';
import { LocalStorageUtils } from '@qality/common-utils';

import { OnboardingTile } from './OnboardingTile';
import {
  BodyStyled,
  ContainerStyled,
  HeaderStyled,
  TitleStyled,
  TitleWrapperStyled,
} from './TestCaseRepositoryOnboarding.styled';

const LOCAL_STORAGE_KEY = 'REPOSITORY_ONBOARDING_v1';

type Visibility = 'expanded' | 'collapsed' | 'hidden';

const ButtonTextMap: Record<Visibility, string> = {
  expanded: 'Hide',
  collapsed: 'Show more',
  hidden: null,
};

const writeExpandedStateToLocalStorage = (value: Visibility) => {
  LocalStorageUtils.storeUserSettings(value, LOCAL_STORAGE_KEY);
};

const readVisibilityFromLocalStorage = () => {
  return LocalStorageUtils.retrieveUserSettings<Visibility>(LOCAL_STORAGE_KEY);
};

export function TestCaseRepositoryOnboarding(): JSX.Element {
  const [visibility, setVisibility] = useState<Visibility>('hidden');

  const ImageService = StorageOfUniqueServices.get('ImageService');

  const tilesContent = [
    {
      src: ImageService.getTestCaseRepositoryPath(),
      text: 'What is the Test Case repository?',
      link: 'https://soldevelo.atlassian.net/l/cp/GV1oHDGH',
      isPlusOnly: false,
    },
    {
      src: ImageService.getFolder(),
      text: 'How to organize Test Cases in folders?',
      link: 'https://soldevelo.atlassian.net/l/cp/X6HXbCBG',
      isPlusOnly: true,
    },
    {
      src: ImageService.getPermissions(),
      text: 'Permissions in the repository',
      link: 'https://soldevelo.atlassian.net/l/cp/pegmvjri',
      isPlusOnly: false,
    },
  ];

  useEffect(() => {
    init();
  }, []);

  const init = (): void => {
    const value = readVisibilityFromLocalStorage();
    setVisibility(value ?? 'expanded');
  };

  const toggleAccordion = (): void => {
    switch (visibility) {
      case 'expanded': {
        collapseAccordion();
        return;
      }
      case 'collapsed': {
        expandAccordion();
        return;
      }
      case 'hidden':
      default:
        break;
    }
  };

  const expandAccordion = () => {
    setVisibility('expanded');
    writeExpandedStateToLocalStorage('expanded');
  };

  const collapseAccordion = () => {
    setVisibility('collapsed');
    writeExpandedStateToLocalStorage('collapsed');
  };

  const hideOnboarding = () => {
    setVisibility('hidden');
    writeExpandedStateToLocalStorage('hidden');
  };

  if (visibility === 'hidden') {
    return null;
  }

  return (
    <ContainerStyled>
      <HeaderStyled>
        <TitleWrapperStyled>
          <PremiumIcon label='' />
          <TitleStyled>
            New test cases organizing experience. Get the most of it!
          </TitleStyled>
        </TitleWrapperStyled>
        <ButtonGroup>
          <Button
            appearance='subtle-link'
            spacing='compact'
            onClick={hideOnboarding}
          >
            Don&apos;t show again
          </Button>
          <Button
            appearance='link'
            spacing='compact'
            onClick={toggleAccordion}
            style={{ marginRight: '-10px' }}
          >
            {ButtonTextMap[visibility]}
          </Button>
        </ButtonGroup>
      </HeaderStyled>
      {visibility === 'expanded' && (
        <BodyStyled>
          {tilesContent.map((content, index) => {
            return (
              <OnboardingTile
                src={content.src}
                text={content.text}
                link={content.link}
                isPlusOnly={content.isPlusOnly}
                key={index}
              />
            );
          })}
        </BodyStyled>
      )}
    </ContainerStyled>
  );
}
